import React, { memo } from 'react';

import { SlicedSuggestions } from 'types/suggestions';
import { useTranslation } from 'react-i18next';
import { useSuggestionsButtonAction } from '../hooks/use-suggestions-button-action';
import { SuggestionListItemServicesPage } from './suggestion-list-item-services-page';
import { SuggestionListItemPromoPage } from './suggestion-list-item-promo-page';
import { SuggestionsList } from './suggestions-list';
import { NotFoundSuggestion } from './not-found-suggestion';
import { SuggestionListItemProduct } from './suggestion-list-item-product';
import { SuggestionListItemServices } from './suggestion-list-item-services';
import { Akinator } from './akinator';

export type SuggestionsProps = {
  searchQuery: string;
  suggestions: SlicedSuggestions | null;
  onMouseEnter: (idx: number) => void;
  onMouseLeave: () => void;
  onSelectSuggestion?: () => void;
  searchHref: string;
  suggestionSelector: string;
  index: number;
  isSrp: boolean;
  notFoundMessage: string | false | undefined;
};

export const Suggestions = memo<SuggestionsProps>(
  ({
    searchQuery,
    suggestions,
    suggestionSelector,
    onMouseEnter,
    onMouseLeave,
    searchHref,
    index,
    isSrp,
    onSelectSuggestion,
    notFoundMessage,
  }) => {
    const [slicedProducts, slicedServices] = suggestions ?? [[], []];
    const buttonAction = useSuggestionsButtonAction({
      onMouseEnter,
      onMouseLeave,
      onSelectSuggestion,
      searchQuery,
    });

    const { t } = useTranslation();

    if (!suggestions) return null;

    if (slicedProducts.length === 0 && slicedServices.length === 0) {
      if (notFoundMessage === false) {
        return null;
      }
      return (
        <NotFoundSuggestion
          notFoundMessage={notFoundMessage}
          searchHref={searchHref}
          searchQuery={searchQuery}
        />
      );
    }

    const isDefaultList = searchQuery === '';

    return (
      <>
        {isDefaultList && (
          <>
            <Akinator />
            <SuggestionsList
              suggestionSelector={suggestionSelector}
              withTitle={false}
              withDivider
              title={t('header.searchbar.top-month')}
              aria-label={t('header.searchbar.top-month')}>
              <SuggestionListItemPromoPage
                elementIndex={0}
                navigationIndex={index}
                isSrp={isSrp}
                suggestionSelector={suggestionSelector}
                buttonAction={buttonAction}
              />
            </SuggestionsList>
            <SuggestionsList
              suggestionSelector={suggestionSelector}
              withTitle={false}
              withDivider
              title={t('header.searchbar.projects-services')}
              aria-label={t('header.searchbar.projects-services')}>
              <SuggestionListItemServicesPage
                elementIndex={1}
                navigationIndex={index}
                isSrp={isSrp}
                suggestionSelector={suggestionSelector}
                buttonAction={buttonAction}
              />
            </SuggestionsList>
          </>
        )}
        <SuggestionsList
          suggestionSelector={suggestionSelector}
          withTitle={false}
          withDivider={isDefaultList}
          title={t('header.searchbar.products')}
          aria-label={t('header.searchbar.products-aria')}>
          {slicedProducts.map((item, idx) => (
            <SuggestionListItemProduct
              productSuggestion={item}
              elementIndex={isDefaultList ? 2 + idx : idx}
              navigationIndex={index}
              searchHref={searchHref}
              buttonAction={buttonAction}
              isSrp={isSrp}
              suggestionSelector={suggestionSelector}
              key={item.phrase}
            />
          ))}
        </SuggestionsList>
        <SuggestionsList
          suggestionSelector={suggestionSelector}
          withTitle={false}
          withDivider={slicedProducts.length > 0 && slicedServices.length > 0}
          title={t('header.searchbar.services')}
          aria-label={t('header.searchbar.services-aria')}>
          {slicedServices.map((item, idx) => (
            <SuggestionListItemServices
              servicesSuggestion={item}
              elementIndex={isDefaultList ? 2 + slicedProducts.length + idx : slicedProducts.length + idx}
              navigationIndex={index}
              buttonAction={buttonAction}
              isSrp={isSrp}
              suggestionSelector={suggestionSelector}
              key={item.serviceId}
            />
          ))}
        </SuggestionsList>
      </>
    );
  }
);
